import { useRef, useState } from "react";
import axios from "axios";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { fileUrl } from "../lib/files";

export const AField = ({ label, testid, hint, ...props }) => (
  <div>
    <label className="block text-[11px] tracking-[0.16em] uppercase font-bold text-forest/60 mb-2">{label}</label>
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-white border border-forest/15 px-4 py-3 text-sm text-ink focus:border-gold focus:outline-none transition-colors"
    />
    {hint && <p className="text-[11px] text-sage mt-2 leading-relaxed">{hint}</p>}
  </div>
);

export const ATextArea = ({ label, testid, rows = 4, hint, ...props }) => (
  <div>
    <label className="block text-[11px] tracking-[0.16em] uppercase font-bold text-forest/60 mb-2">{label}</label>
    <textarea
      data-testid={testid}
      rows={rows}
      {...props}
      className="w-full bg-white border border-forest/15 px-4 py-3 text-sm text-ink focus:border-gold focus:outline-none transition-colors resize-y"
    />
    {hint && <p className="text-[11px] text-sage mt-2 leading-relaxed">{hint}</p>}
  </div>
);

export const ASelect = ({ label, testid, options, ...props }) => (
  <div>
    <label className="block text-[11px] tracking-[0.16em] uppercase font-bold text-forest/60 mb-2">{label}</label>
    <select
      data-testid={testid}
      {...props}
      className="w-full bg-white border border-forest/15 px-4 py-3 text-sm text-ink focus:border-gold focus:outline-none transition-colors"
    >
      {options.map((o) => {
        const opt = typeof o === "string" ? { value: o, label: o } : o;
        return (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        );
      })}
    </select>
  </div>
);

export const ImageUpload = ({ label, value, onChange, testid, hint, minWidth = 400, trim = false }) => {
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState("");
  const inputRef = useRef(null);

  // Probe raster image dimensions client-side so we can nudge admins to
  // upload a higher-resolution asset. SVGs are vector so we skip the check.
  const probeDimensions = (file) =>
    new Promise((resolve) => {
      if (!file.type || !file.type.startsWith("image/") || file.type === "image/svg+xml") {
        resolve(null);
        return;
      }
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setWarning("");
    const dims = await probeDimensions(f);
    if (dims && dims.w < minWidth) {
      setWarning(
        `Low-resolution image detected (${dims.w}×${dims.h}px). For a crisp result on large and retina screens, upload a PNG or SVG with a transparent background that is at least ${minWidth}px wide.`
      );
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const url = trim ? `${API}/admin/upload?trim=1` : `${API}/admin/upload`;
      const { data } = await axios.post(url, fd, authHeaders());
      onChange(data.path);
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="block text-[11px] tracking-[0.16em] uppercase font-bold text-forest/60 mb-2">{label}</label>
      <div className="flex items-center gap-4 flex-wrap">
        {value ? (
          <img src={fileUrl(value)} alt={label} className="h-20 w-32 object-cover border border-forest/15 bg-white" />
        ) : (
          <div className="h-20 w-32 border border-dashed border-forest/25 flex items-center justify-center text-[10px] tracking-wide uppercase text-sage">
            No image
          </div>
        )}
        <div>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-file`} />
          <button
            type="button"
            data-testid={testid}
            onClick={() => inputRef.current.click()}
            disabled={busy}
            className="btn-forest !py-3 text-[11px] disabled:opacity-60"
          >
            {busy ? "Uploading..." : value ? "Replace Image" : "Upload Image"}
          </button>
          {hint && <p className="text-[11px] text-sage mt-2 max-w-xs">{hint}</p>}
        </div>
      </div>
      {warning && (
        <p
          data-testid={`${testid}-warning`}
          className="mt-3 bg-amber-50 border border-amber-200 text-amber-800 text-[12px] px-3 py-2 rounded leading-relaxed max-w-md"
        >
          {warning}
        </p>
      )}
    </div>
  );
};

export const SectionShell = ({ title, subtitle, action, children, testid }) => (
  <section data-testid={testid}>
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl font-medium text-forest">{title}</h1>
        {subtitle && <p className="text-[13px] text-sage mt-2 max-w-xl leading-relaxed">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

export const SaveButton = ({ saving, testid, label = "Save Changes" }) => (
  <button type="submit" data-testid={testid} disabled={saving} className="btn-gold !py-3.5 disabled:opacity-60">
    {saving ? "Saving..." : label}
  </button>
);

export const LoadError = ({ onRetry, message = "We couldn't load this page.", testid = "admin-load-error" }) => (
  <div
    data-testid={testid}
    className="bg-white border border-red-200 px-6 py-10 text-center max-w-xl mx-auto"
  >
    <p className="font-serif text-xl text-forest mb-2">Something went wrong</p>
    <p className="text-sm text-sage mb-5">{message} Please check your connection and try again.</p>
    {onRetry && (
      <button
        type="button"
        data-testid={`${testid}-retry`}
        onClick={onRetry}
        className="btn-gold"
      >
        Retry
      </button>
    )}
  </div>
);

export const Notice = ({ error, success }) => (
  <>
    {error && (
      <p data-testid="admin-error" className="bg-red-50 border border-red-200 text-red-700 text-[13px] px-4 py-3 mb-6">{error}</p>
    )}
    {success && (
      <p data-testid="admin-success" className="bg-forest/5 border border-forest/20 text-forest text-[13px] px-4 py-3 mb-6">{success}</p>
    )}
  </>
);
